import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Loader2 } from 'lucide-react';

/**
 * LazyYandexMap - компонент для отложенной загрузки Яндекс.Карт
 * Карта загружается только когда пользователь прокрутит до неё
 */
const LazyYandexMap = ({
  src,
  width = '100%',
  height = '400',
  title = 'Яндекс карта',
  address = '',
  rootMargin = '200px',
}) => {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0,
        rootMargin,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [rootMargin]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  return (
    <div
      ref={containerRef}
      className="relative rounded-2xl overflow-hidden shadow-lg bg-gray-100"
      style={{ width, height: typeof height === 'number' ? `${height}px` : height }}
    >
      {/* Placeholder до загрузки карты */}
      {!isLoaded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-emerald-50 to-green-100">
          {isInView ? (
            <>
              <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mb-3" />
              <p className="text-emerald-700 font-medium">Загрузка карты...</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-gray-600 text-center px-4 max-w-md">
                {address || 'Прокрутите вниз для загрузки карты'}
              </p>
            </>
          )}
        </div>
      )}

      {/* Iframe загружается только когда компонент в viewport */}
      {isInView && (
        <iframe
          src={src}
          width="100%"
          height="100%"
          frameBorder="0"
          allowFullScreen={true}
          onLoad={handleLoad}
          title={title}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
        />
      )}
    </div>
  );
};

export default LazyYandexMap;
